import { Sprite, type Container } from 'pixi.js';
import { BUILDING_DEFS, MAP_H, MAP_W, colOf, xOf, yOf } from '../sim/constants';
import type { SimState } from '../sim/types';
import { FLAG_BUILDING } from '../sim/types';
import { hasFlag } from '../sim/world/terrain';
import { accessTiles, findPath } from '../sim/world/path';
import type { GrayboxTextures } from './graybox';
import { ELEV_STEP, HALF_H, HALF_W } from './iso';

type VehicleKind = 'oreTruck' | 'goodsWagon';

/** Tiles per sim-second — peeps walk 3.0 (×1.6 on roads); trucks outpace them. */
const TILES_PER_SEC = 7;
const LOADING_SECONDS = 1.5;
/** Cosmetic search only — never let an odd map flood the whole grid. */
const PATH_MAX_NODES = 4000;

interface Vehicle {
  kind: VehicleKind;
  sprite: Sprite;
  /** Edge col → depot access col, inclusive of both ends. */
  path: number[];
  phase: 'inbound' | 'loading' | 'outbound';
  /** Fractional index into path; outbound walks it backwards. */
  progress: number;
  loadingUntil: number;
}

/**
 * Purely cosmetic vehicles: when the sim reports a truck departure, one drives
 * in from the map edge, pauses at the depot to load, and drives away. All the
 * real economy already happened in the sim — this exists so the sale is a
 * thing the player can SEE. Motion runs on the sim clock, so pausing freezes
 * the truck and fast-forward speeds it along with the rest of the world.
 */
export class VehicleLayer {
  private readonly tex: GrayboxTextures;
  private readonly parent: Container;
  private readonly vehicles: Vehicle[] = [];
  private readonly pendingSpawns: VehicleKind[] = [];
  private lastSimSeconds: number | null = null;

  constructor(tex: GrayboxTextures, parent: Container) {
    this.tex = tex;
    this.parent = parent;
  }

  /** Called from the renderer's event pump — state isn't in scope there. */
  notifyDeparture(kind: VehicleKind): void {
    this.pendingSpawns.push(kind);
  }

  update(state: SimState, simSeconds: number): void {
    const dt = this.lastSimSeconds === null ? 0 : Math.max(0, simSeconds - this.lastSimSeconds);
    this.lastSimSeconds = simSeconds;

    while (this.pendingSpawns.length > 0) {
      const kind = this.pendingSpawns.shift()!;
      const vehicle = this.spawn(state, kind, simSeconds);
      if (vehicle) this.vehicles.push(vehicle);
    }

    for (let index = this.vehicles.length - 1; index >= 0; index -= 1) {
      const vehicle = this.vehicles[index]!;
      if (vehicle.phase === 'loading') {
        if (simSeconds >= vehicle.loadingUntil) {
          vehicle.phase = 'outbound';
          vehicle.progress = vehicle.path.length - 1;
        }
      } else if (vehicle.phase === 'inbound') {
        vehicle.progress += dt * TILES_PER_SEC;
        if (vehicle.progress >= vehicle.path.length - 1) {
          vehicle.progress = vehicle.path.length - 1;
          vehicle.phase = 'loading';
          vehicle.loadingUntil = simSeconds + LOADING_SECONDS;
        }
      } else {
        vehicle.progress -= dt * TILES_PER_SEC;
        if (vehicle.progress <= 0) {
          vehicle.sprite.destroy();
          this.vehicles.splice(index, 1);
          continue;
        }
      }
      this.position(state, vehicle);
    }
  }

  private spawn(state: SimState, kind: VehicleKind, simSeconds: number): Vehicle | null {
    const targetType = kind === 'goodsWagon' ? 'freightOffice' : 'oreDepot';
    const depot =
      state.buildings.find((b) => b.type === targetType) ??
      state.buildings.find((b) => b.type === 'oreDepot');
    if (!depot) return null;
    const def = BUILDING_DEFS[depot.type];
    const targets = accessTiles(state.terrain, depot.x, depot.y, def.w, def.h);
    if (targets.size === 0) return null;

    const edgeCol = this.edgeColNear(state, depot.x, depot.y);
    const found = edgeCol === null ? null : findPath(state.terrain, edgeCol, targets, PATH_MAX_NODES);
    // No route? Glide straight in — a truck that cuts a corner beats no truck.
    const path =
      edgeCol !== null && found !== null && found.length > 0
        ? [edgeCol, ...found]
        : edgeCol !== null
          ? [edgeCol, [...targets][0]!]
          : [[...targets][0]!];
    if (path.length < 2) return null;

    const sprite = new Sprite(this.tex.vehicle(kind));
    sprite.anchor.set(0.5, 1);
    this.parent.addChild(sprite);
    const vehicle: Vehicle = { kind, sprite, path, phase: 'inbound', progress: 0, loadingUntil: simSeconds };
    this.position(state, vehicle);
    return vehicle;
  }

  /** Nearest map-edge col to the depot, nudged along the edge off any building. */
  private edgeColNear(state: SimState, x: number, y: number): number | null {
    const candidates: [number, number][] = [
      [0, y],
      [MAP_W - 1, y],
      [x, 0],
      [x, MAP_H - 1],
    ];
    candidates.sort(
      (a, b) => Math.abs(a[0] - x) + Math.abs(a[1] - y) - (Math.abs(b[0] - x) + Math.abs(b[1] - y)),
    );
    for (const [ex, ey] of candidates) {
      for (let nudge = 0; nudge <= 3; nudge += 1) {
        for (const sign of nudge === 0 ? [1] : [1, -1]) {
          const nx = ex === 0 || ex === MAP_W - 1 ? ex : Math.min(MAP_W - 1, Math.max(0, ex + nudge * sign));
          const ny = ey === 0 || ey === MAP_H - 1 ? ey : Math.min(MAP_H - 1, Math.max(0, ey + nudge * sign));
          const col = colOf(nx, ny);
          if (!hasFlag(state.terrain, col, FLAG_BUILDING)) return col;
        }
      }
    }
    return null;
  }

  private position(state: SimState, vehicle: Vehicle): void {
    const last = vehicle.path.length - 1;
    const at = Math.min(Math.max(vehicle.progress, 0), last);
    const from = vehicle.path[Math.floor(at)]!;
    const to = vehicle.path[Math.min(Math.ceil(at), last)]!;
    const t = at - Math.floor(at);
    const fx = xOf(from) + (xOf(to) - xOf(from)) * t + 0.5;
    const fy = yOf(from) + (yOf(to) - yOf(from)) * t + 0.5;
    const depthFrom = state.terrain.depth[from] ?? 0;
    const depthTo = state.terrain.depth[to] ?? 0;
    const depth = depthFrom + (depthTo - depthFrom) * t;
    const screenX = (fx - fy) * HALF_W;
    vehicle.sprite.position.set(screenX, (fx + fy) * HALF_H + depth * ELEV_STEP);
    vehicle.sprite.zIndex = (fx + fy) * 16 + 6;
    // Face the direction of travel; the texture looks +x (screen right).
    const dxScreen = (xOf(to) - xOf(from) - (yOf(to) - yOf(from))) * (vehicle.phase === 'outbound' ? -1 : 1);
    if (dxScreen !== 0) vehicle.sprite.scale.x = dxScreen > 0 ? 1 : -1;
  }
}
